// Canvas 2D bioluminescent mycelium network: growing filaments, travelling light pulses, shockwaves, bloom.
export const VISUAL_PALETTES = {
  title: { base: [0, 140, 110], glow: [0, 255, 157], speed: 160, jitter: 2, breathHz: 0.12 },
  curiosity: { base: [0, 130, 150], glow: [0, 229, 255], speed: 210, jitter: 2.5, breathHz: 0.15 },
  fear: { base: [150, 30, 40], glow: [255, 77, 77], speed: 430, jitter: 6, breathHz: 0.55 },
  trust: { base: [30, 120, 90], glow: [140, 255, 200], speed: 140, jitter: 1.2, breathHz: 0.1 },
  joy: { base: [170, 115, 0], glow: [255, 200, 40], speed: 360, jitter: 3, breathHz: 0.35 },
  grief: { base: [40, 60, 110], glow: [120, 150, 235], speed: 90, jitter: 1, breathHz: 0.06 },
  unity: { base: [90, 60, 160], glow: [210, 170, 255], speed: 260, jitter: 2, breathHz: 0.2, cycle: true },
};
const CYCLE = ['curiosity', 'fear', 'trust', 'joy', 'grief'].map((k) => VISUAL_PALETTES[k].glow);
const lerp = (a, b, k) => a + (b - a) * k;
const rgba = (c, a) => `rgba(${c[0] | 0},${c[1] | 0},${c[2] | 0},${a})`;

export function createMycelium(canvas) {
  const ctx = canvas.getContext('2d');
  let w = 0;
  let h = 0;
  let nodes = [];
  let edges = [];
  let pulses = [];
  let rings = [];
  let target = VISUAL_PALETTES.title;
  const col = { base: [...target.base], glow: [...target.glow], speed: target.speed, jitter: target.jitter, breathHz: target.breathHz };
  let bloom = 0;
  let flash = 0;
  let bright = 0.6;
  let lastT = performance.now();
  let lastGrow = 0;
  let raf = null;
  let sig = { intensity: 0, activity: 0, agitation: 0, stillnessSeconds: 0 };

  function addNode(x, y, angle, depth, parent, born) {
    const n = { x, y, angle, depth, children: [] };
    nodes.push(n);
    if (parent !== null) {
      const p = nodes[parent];
      const len = Math.hypot(x - p.x, y - p.y);
      const off = (Math.random() - 0.5) * len * 0.6;
      edges.push({ a: parent, b: nodes.length - 1, len, ox: -Math.sin(angle) * off, oy: Math.cos(angle) * off, phase: Math.random() * 6.28, born, depth });
      p.children.push(edges.length - 1);
    }
  }

  function growOne(born) {
    for (let tries = 0; tries < 16; tries += 1) {
      const pi = tries < 10 ? nodes.length - 1 - Math.floor(Math.random() * Math.min(nodes.length, 260)) : Math.floor(Math.random() * nodes.length);
      const p = nodes[pi];
      if (p.children.length >= (Math.random() < 0.18 ? 3 : 1)) continue;
      const angle = p.angle + (Math.random() - 0.5) * 0.9;
      const len = 22 + Math.random() * 40;
      const x = p.x + Math.cos(angle) * len;
      const y = p.y + Math.sin(angle) * len;
      if (x < 10 || y < 10 || x > w - 10 || y > h - 10) continue;
      addNode(x, y, angle, p.depth + 1, pi, born);
      return;
    }
  }

  function build() {
    nodes = [];
    edges = [];
    pulses = [];
    const cx = w * 0.5;
    const cy = h * 0.56;
    addNode(cx, cy, 0, 0, null, -1e9);
    for (let i = 0; i < 14; i += 1) {
      const a = (i / 14) * Math.PI * 2 + Math.random() * 0.3;
      addNode(cx + Math.cos(a) * 26, cy + Math.sin(a) * 26, a, 1, 0, -1e9);
    }
    const target0 = Math.min(1400, Math.round((w * h) / 1500));
    while (nodes.length < target0 * 0.7) growOne(-1e9);
  }

  function resize() {
    const dpr = Math.min(1.5, window.devicePixelRatio || 1);
    w = canvas.clientWidth;
    h = canvas.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    build();
  }

  const ctrl = (e, t) => {
    const a = nodes[e.a];
    const b = nodes[e.b];
    const wob = Math.sin(t * col.breathHz * 6.28 + e.phase) * col.jitter;
    return [(a.x + b.x) / 2 + e.ox + wob, (a.y + b.y) / 2 + e.oy + wob * 0.6];
  };

  function spawn(n, { speed = 1, size = 2, glow = 1 } = {}) {
    const rootEdges = nodes[0].children;
    for (let i = 0; i < n && pulses.length < 450; i += 1) {
      const start = rootEdges[Math.floor(Math.random() * rootEdges.length)];
      pulses.push({ e: start, p: 0, v: col.speed * speed * (0.7 + Math.random() * 0.6), size, glow, life: 6 });
    }
  }

  function step(t, dt) {
    const k = Math.min(1, dt * 0.8);
    const tg = target.cycle ? CYCLE[Math.floor(t / 3) % CYCLE.length] : target.glow;
    for (let i = 0; i < 3; i += 1) {
      col.base[i] = lerp(col.base[i], target.base[i], k);
      col.glow[i] = lerp(col.glow[i], tg[i], k);
    }
    col.speed = lerp(col.speed, target.speed, k);
    col.jitter = lerp(col.jitter + sig.agitation * 0.2, target.jitter, k);
    col.breathHz = lerp(col.breathHz, target.breathHz, k);
    const stillDim = Math.min(0.35, sig.stillnessSeconds / 30);
    bright = lerp(bright, 0.5 + sig.intensity * 0.5 - stillDim, Math.min(1, dt * 2));
    bloom = Math.max(0, bloom - dt * 0.3);
    flash = Math.max(0, flash - dt * 2.5);
    if (Math.random() < dt * (sig.intensity * 22 + sig.activity * 10)) spawn(1, { size: 1.6 + sig.intensity * 2 });
    if (t - lastGrow > 0.25 && nodes.length < Math.min(1800, (w * h) / 1100)) {
      growOne(t);
      lastGrow = t;
    }
    pulses = pulses.filter((pl) => {
      pl.life -= dt;
      pl.p += (pl.v * dt) / edges[pl.e].len;
      while (pl.p >= 1) {
        const kids = nodes[edges[pl.e].b].children;
        if (!kids.length) return false;
        pl.p -= 1;
        pl.e = kids[Math.floor(Math.random() * kids.length)];
      }
      return pl.life > 0;
    });
    rings = rings.filter((r) => {
      r.r += r.v * dt;
      r.a -= dt * r.fade;
      return r.a > 0;
    });
  }

  function draw(t) {
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = '#05080c';
    ctx.fillRect(0, 0, w, h);
    const breath = 0.82 + 0.18 * Math.sin(t * col.breathHz * 6.28);
    const alpha = Math.max(0.15, bright * breath + bloom * 0.6);
    const buckets = [[], [], []];
    edges.forEach((e) => buckets[e.depth < 4 ? 0 : e.depth < 14 ? 1 : 2].push(e));
    ctx.globalCompositeOperation = 'lighter';
    ctx.lineCap = 'round';
    [[2.4, 1], [1.3, 0.75], [0.7, 0.5]].forEach(([lw, la], bi) => {
      ctx.beginPath();
      buckets[bi].forEach((e) => {
        const a = nodes[e.a];
        const b = nodes[e.b];
        const g = Math.min(1, (t - e.born) / 1.2);
        const [cx, cy] = ctrl(e, t);
        ctx.moveTo(a.x, a.y);
        if (g >= 1) ctx.quadraticCurveTo(cx, cy, b.x, b.y);
        else ctx.lineTo(lerp(a.x, b.x, g), lerp(a.y, b.y, g));
      });
      ctx.strokeStyle = rgba(col.glow, 0.07 * alpha * la);
      ctx.lineWidth = lw * 5;
      ctx.stroke();
      ctx.strokeStyle = rgba(col.base, 0.55 * alpha * la + bloom * 0.3);
      ctx.lineWidth = lw;
      ctx.stroke();
    });
    pulses.forEach((pl) => {
      const e = edges[pl.e];
      const a = nodes[e.a];
      const b = nodes[e.b];
      const [cx, cy] = ctrl(e, t);
      const u = pl.p;
      const x = (1 - u) * (1 - u) * a.x + 2 * (1 - u) * u * cx + u * u * b.x;
      const y = (1 - u) * (1 - u) * a.y + 2 * (1 - u) * u * cy + u * u * b.y;
      const fade = Math.min(1, pl.life / 1.5);
      ctx.fillStyle = rgba(col.glow, 0.12 * pl.glow * fade);
      ctx.beginPath();
      ctx.arc(x, y, pl.size * 4, 0, 6.28);
      ctx.fill();
      ctx.fillStyle = rgba([255, 255, 255], 0.75 * fade);
      ctx.beginPath();
      ctx.arc(x, y, pl.size * 0.8, 0, 6.28);
      ctx.fill();
    });
    rings.forEach((r) => {
      ctx.strokeStyle = rgba(col.glow, Math.max(0, r.a));
      ctx.lineWidth = r.lw;
      ctx.beginPath();
      ctx.arc(nodes[0].x, nodes[0].y, r.r, 0, 6.28);
      ctx.stroke();
    });
    const core = 18 + sig.intensity * 30 + flash * 40 + bloom * 60;
    const grad = ctx.createRadialGradient(nodes[0].x, nodes[0].y, 0, nodes[0].x, nodes[0].y, core * 3);
    grad.addColorStop(0, rgba(col.glow, 0.5 * alpha + flash * 0.5));
    grad.addColorStop(1, rgba(col.glow, 0));
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(nodes[0].x, nodes[0].y, core * 3, 0, 6.28);
    ctx.fill();
    if (flash > 0) {
      ctx.fillStyle = rgba(col.glow, flash * 0.08);
      ctx.fillRect(0, 0, w, h);
    }
  }

  function loop() {
    const nowT = performance.now();
    const dt = Math.min(0.1, (nowT - lastT) / 1000);
    lastT = nowT;
    const t = nowT / 1000;
    step(t, dt);
    draw(t);
    raf = requestAnimationFrame(loop);
  }

  resize();
  window.addEventListener('resize', resize);
  loop();

  return {
    setPalette(name) {
      target = VISUAL_PALETTES[name] || VISUAL_PALETTES.title;
    },
    setSignal(s) {
      sig = s;
    },
    touch(velocity) {
      spawn(1 + Math.round(velocity / 60), { size: 1.4 + velocity / 60 });
    },
    gesture(g) {
      if (g.type === 'PULSE' && g.solid) {
        rings.push({ r: 10, v: 1300, a: 0.9, fade: 0.9, lw: 3 });
        flash = 1;
        spawn(40, { speed: 2.2, size: 2.6, glow: 1.6 });
      } else if (g.type === 'PULSE') {
        rings.push({ r: 10, v: 900, a: 0.35, fade: 0.8, lw: 1.5 });
        spawn(12, { speed: 1.6, size: 2 });
      } else if (g.type === 'STROKE') {
        rings.push({ r: 10, v: 240, a: 0.22, fade: 0.25, lw: 14 });
        spawn(8, { speed: 0.7, size: 1.8 });
      }
    },
    bloom() {
      bloom = 1;
      flash = 1;
      rings.push({ r: 10, v: 700, a: 1, fade: 0.35, lw: 6 });
      spawn(160, { speed: 1.5, size: 2.4, glow: 1.5 });
    },
    destroy() {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    },
  };
}
